/**
 * Guest Taška★ bag (browser-only) — Wave 5 / NEW G8.
 * Key `customer:pendingOfferFavorites`, max 20, TTL 90d.
 * Separate from `customer:pendingFavoriteSpContacts` (no collision).
 * Claimed via `transferGuestOfferFavoritesSilent` on establish; LS retained.
 */

import type {
  ClaimCustomerOfferFavoriteRef,
  OfferFavoriteGrainKey,
} from './offerFavoritesTypes.js';
import { offerFavoriteGrainKey } from './offerFavoritesTypes.js';

export const PENDING_OFFER_FAVORITES_STORAGE_KEY = 'customer:pendingOfferFavorites';
export const PENDING_OFFER_FAVORITES_CHANGED_EVENT =
  'customer:pendingOfferFavorites-changed';
export const PENDING_OFFER_FAVORITES_MAX = 20;
export const PENDING_OFFER_FAVORITES_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Display snapshot so Taška★ works offline without a list round-trip. */
export interface PendingOfferFavorite extends ClaimCustomerOfferFavoriteRef {
  title: string;
  name: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  price: number | null;
  salesPointName: string;
  salesPointCode: string;
  tenantId: number;
  tenantName: string;
}

interface StoredPendingOfferFavorite extends PendingOfferFavorite {
  storedAt: number;
}

function notifyPendingOfferFavoritesChanged(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(PENDING_OFFER_FAVORITES_CHANGED_EVENT));
}

export function subscribePendingOfferFavorites(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  window.addEventListener(PENDING_OFFER_FAVORITES_CHANGED_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(PENDING_OFFER_FAVORITES_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

export function getPendingOfferFavoritesSnapshot(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(PENDING_OFFER_FAVORITES_STORAGE_KEY) ?? '';
}

export function getPendingOfferFavoritesServerSnapshot(): string {
  return '';
}

function dedupeKey(item: ClaimCustomerOfferFavoriteRef): string {
  if (typeof item.salesPointId === 'number' && item.salesPointId > 0) {
    return offerFavoriteGrainKey({
      salesPointId: item.salesPointId,
      productId: item.productId,
      variantId: item.variantId ?? null,
    });
  }
  const slug = (item.salesPointSlug ?? '').trim().toLowerCase();
  const variant =
    item.variantId === null || item.variantId === undefined
      ? 'null'
      : String(item.variantId);
  return `slug:${item.tenantCode.trim().toLowerCase()}|${slug}|${item.productId}|${variant}`;
}

function isExpired(storedAt: number, nowMs: number): boolean {
  return nowMs - storedAt > PENDING_OFFER_FAVORITES_TTL_MS;
}

function readRaw(): StoredPendingOfferFavorite[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(PENDING_OFFER_FAVORITES_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is StoredPendingOfferFavorite => {
      if (item === null || typeof item !== 'object') {
        return false;
      }
      const row = item as StoredPendingOfferFavorite;
      return (
        typeof row.tenantCode === 'string' &&
        row.tenantCode.length > 0 &&
        typeof row.productId === 'number' &&
        row.productId > 0 &&
        typeof row.seenAt === 'string' &&
        typeof row.storedAt === 'number' &&
        typeof row.title === 'string' &&
        typeof row.name === 'string' &&
        typeof row.salesPointName === 'string' &&
        typeof row.salesPointCode === 'string' &&
        typeof row.tenantId === 'number' &&
        typeof row.tenantName === 'string' &&
        (typeof row.salesPointId === 'number' ||
          (typeof row.salesPointSlug === 'string' && row.salesPointSlug.length > 0))
      );
    });
  } catch {
    return [];
  }
}

function writeRaw(items: StoredPendingOfferFavorite[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      PENDING_OFFER_FAVORITES_STORAGE_KEY,
      JSON.stringify(items),
    );
    notifyPendingOfferFavoritesChanged();
  } catch {
    /* quota / private mode */
  }
}

function pruneAndDedupe(
  items: StoredPendingOfferFavorite[],
  nowMs: number,
): StoredPendingOfferFavorite[] {
  const fresh = items.filter((item) => !isExpired(item.storedAt, nowMs));
  const byKey = new Map<string, StoredPendingOfferFavorite>();
  for (const item of fresh) {
    byKey.set(dedupeKey(item), item);
  }
  return Array.from(byKey.values())
    .sort((a, b) => b.storedAt - a.storedAt)
    .slice(0, PENDING_OFFER_FAVORITES_MAX);
}

function toPublic(item: StoredPendingOfferFavorite): PendingOfferFavorite {
  return {
    tenantCode: item.tenantCode,
    productId: item.productId,
    seenAt: item.seenAt,
    title: item.title,
    name: item.name,
    imageUrl: item.imageUrl,
    thumbnailUrl: item.thumbnailUrl,
    price: item.price,
    salesPointName: item.salesPointName,
    salesPointCode: item.salesPointCode,
    tenantId: item.tenantId,
    tenantName: item.tenantName,
    ...(typeof item.salesPointId === 'number' ? { salesPointId: item.salesPointId } : {}),
    ...(typeof item.salesPointSlug === 'string' && item.salesPointSlug.length > 0
      ? { salesPointSlug: item.salesPointSlug }
      : {}),
    ...(item.variantId !== undefined ? { variantId: item.variantId } : {}),
  };
}

export function readPendingOfferFavorites(
  nowMs: number = Date.now(),
): PendingOfferFavorite[] {
  const existing = readRaw();
  const pruned = pruneAndDedupe(existing, nowMs);
  if (pruned.length !== existing.length) {
    writeRaw(pruned);
  }
  return pruned.map(toPublic);
}

export function readPendingOfferFavoriteRefs(
  nowMs: number = Date.now(),
): ClaimCustomerOfferFavoriteRef[] {
  return readPendingOfferFavorites(nowMs).map(
    ({
      tenantCode,
      salesPointId,
      salesPointSlug,
      productId,
      variantId,
      seenAt,
    }) => ({
      tenantCode,
      productId,
      seenAt,
      ...(typeof salesPointId === 'number' ? { salesPointId } : {}),
      ...(typeof salesPointSlug === 'string' && salesPointSlug.length > 0
        ? { salesPointSlug }
        : {}),
      ...(variantId !== undefined ? { variantId } : {}),
    }),
  );
}

export function isPendingOfferFavoriteGrain(
  grain: OfferFavoriteGrainKey,
  nowMs: number = Date.now(),
): boolean {
  const key = offerFavoriteGrainKey(grain);
  return readPendingOfferFavorites(nowMs).some((item) => {
    if (typeof item.salesPointId !== 'number') {
      return false;
    }
    return (
      offerFavoriteGrainKey({
        salesPointId: item.salesPointId,
        productId: item.productId,
        variantId: item.variantId ?? null,
      }) === key
    );
  });
}

export type StagePendingOfferFavoriteInput = Omit<PendingOfferFavorite, 'seenAt'> & {
  seenAt?: string;
  nowMs?: number;
};

export function stagePendingOfferFavorite(input: StagePendingOfferFavoriteInput): void {
  const nowMs = input.nowMs ?? Date.now();
  const seenAt = input.seenAt ?? new Date(nowMs).toISOString();
  const next: StoredPendingOfferFavorite = {
    tenantCode: input.tenantCode,
    productId: input.productId,
    seenAt,
    storedAt: nowMs,
    title: input.title,
    name: input.name,
    imageUrl: input.imageUrl,
    thumbnailUrl: input.thumbnailUrl,
    price: input.price,
    salesPointName: input.salesPointName,
    salesPointCode: input.salesPointCode,
    tenantId: input.tenantId,
    tenantName: input.tenantName,
    ...(typeof input.salesPointId === 'number' && input.salesPointId > 0
      ? { salesPointId: input.salesPointId }
      : {}),
    ...(typeof input.salesPointSlug === 'string' && input.salesPointSlug.trim().length > 0
      ? { salesPointSlug: input.salesPointSlug.trim() }
      : {}),
    ...(input.variantId !== undefined ? { variantId: input.variantId } : {}),
  };
  if (
    next.salesPointId === undefined &&
    (next.salesPointSlug === undefined || next.salesPointSlug.length === 0)
  ) {
    return;
  }
  writeRaw(pruneAndDedupe([next, ...readRaw()], nowMs));
}

export function removePendingOfferFavorite(
  grain: OfferFavoriteGrainKey,
  nowMs: number = Date.now(),
): void {
  const key = offerFavoriteGrainKey(grain);
  writeRaw(
    pruneAndDedupe(readRaw(), nowMs).filter((item) => {
      if (typeof item.salesPointId !== 'number') {
        return true;
      }
      return (
        offerFavoriteGrainKey({
          salesPointId: item.salesPointId,
          productId: item.productId,
          variantId: item.variantId ?? null,
        }) !== key
      );
    }),
  );
}

/** After claim — drop activated grains from guest bag store (optional; plan retains LS). */
export function removeActivatedPendingOfferFavorites(
  activated: ReadonlyArray<{
    salesPointId: number;
    productId: number;
    variantId: number | null;
  }>,
  nowMs: number = Date.now(),
): void {
  if (activated.length === 0) {
    writeRaw(pruneAndDedupe(readRaw(), nowMs));
    return;
  }
  const activatedKeys = new Set(
    activated.map((row) =>
      offerFavoriteGrainKey({
        salesPointId: row.salesPointId,
        productId: row.productId,
        variantId: row.variantId,
      }),
    ),
  );
  writeRaw(
    pruneAndDedupe(readRaw(), nowMs).filter((item) => {
      if (typeof item.salesPointId !== 'number') {
        return true;
      }
      return !activatedKeys.has(
        offerFavoriteGrainKey({
          salesPointId: item.salesPointId,
          productId: item.productId,
          variantId: item.variantId ?? null,
        }),
      );
    }),
  );
}

export function clearPendingOfferFavorites(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_OFFER_FAVORITES_STORAGE_KEY);
    notifyPendingOfferFavoritesChanged();
  } catch {
    /* ignore */
  }
}

export { dedupeKey as pendingOfferFavoriteDedupeKey };
